/**
 * The font library, as this person sees it: the families offered to every
 * report, and the Google families fetched only to stand in for a font an
 * imported PDF used.
 *
 * Loaded once, on the first need of it (a report set in a library family, a
 * typeface chooser opened), and kept: the library changes rarely, and a
 * chooser opened again fetches it again (`load(true)`).
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as api from '@/api/fonts'
import type { FontCategory, LibraryFamily } from '@/api/fonts'
import { ApiError, NetworkError } from '@/api/http'
import { t } from '@/i18n'
import { useSessionStore } from '@/stores/session'

export const useFontLibraryStore = defineStore('fontLibrary', () => {
  const session = useSessionStore()

  const families = ref<LibraryFamily[]>([])
  const standIns = ref<LibraryFamily[]>([])
  const loaded = ref(false)
  const busy = ref(false)
  const problem = ref<string | null>(null)

  let loading: Promise<void> | null = null
  const fetchingStandIns = new Map<string, Promise<LibraryFamily | null>>()

  /** This person's families first, then the firm's, each most recent first. */
  const library = computed(() => [
    ...families.value.filter((family) => session.owns(family.owner)),
    ...families.value.filter((family) => !session.owns(family.owner)),
  ])

  function isMine(family: LibraryFamily): boolean {
    return session.owns(family.owner)
  }

  function describe(error: unknown): string {
    if (error instanceof ApiError) return error.message
    if (error instanceof NetworkError) return t('common.unreachable')
    return t('common.problem')
  }

  async function load(force = false): Promise<void> {
    if (loading && !force) return loading
    loading = (async () => {
      try {
        families.value = await api.listFonts()
        loaded.value = true
        problem.value = null
      } catch (error) {
        problem.value = describe(error)
        loading = null
      }
    })()
    return loading
  }

  /** A family by its id: one of the library's, or a stand-in fetched before. */
  async function family(id: number): Promise<LibraryFamily | null> {
    if (!loaded.value) await load()
    return (
      families.value.find((one) => one.id === id) ??
      standIns.value.find((one) => one.id === id) ??
      null
    )
  }

  function put(family: LibraryFamily): void {
    families.value = [family, ...families.value.filter((one) => one.id !== family.id)]
  }

  async function change<T>(work: () => Promise<T>): Promise<T | null> {
    busy.value = true
    problem.value = null
    try {
      return await work()
    } catch (error) {
      problem.value = describe(error)
      return null
    } finally {
      busy.value = false
    }
  }

  async function upload(
    files: readonly File[],
    fields: { name?: string; category?: FontCategory },
  ): Promise<LibraryFamily | null> {
    const created = await change(() => api.uploadFonts(files, fields))
    if (created) put(created)
    return created
  }

  async function addFiles(id: number, files: readonly File[]): Promise<LibraryFamily | null> {
    const updated = await change(() => api.addFontFiles(id, files))
    if (updated) put(updated)
    return updated
  }

  async function rename(
    id: number,
    changes: { name?: string; category?: FontCategory },
  ): Promise<LibraryFamily | null> {
    const updated = await change(() => api.updateFont(id, changes))
    if (updated) families.value = families.value.map((one) => (one.id === id ? updated : one))
    return updated
  }

  async function remove(id: number): Promise<boolean> {
    const deleted = await change(() => api.deleteFont(id))
    if (deleted) families.value = families.value.filter((one) => one.id !== id)
    return deleted === true
  }

  /** A Google family added to the library, fetched by the server if nobody had yet. */
  async function addGoogle(name: string): Promise<LibraryFamily | null> {
    const added = await change(() => api.addGoogleFont(name, true))
    if (added) {
      put(added)
      standIns.value = standIns.value.filter((one) => one.id !== added.id)
    }
    return added
  }

  /**
   * A Google family to stand in for a font an imported PDF used, fetched once
   * per name and kept out of the library. Null when it cannot be had (Google
   * Fonts turned off, a name Google does not know): the page's own fallback
   * draws instead, as it always has.
   */
  function standIn(name: string): Promise<LibraryFamily | null> {
    const listed = families.value.find((one) => one.googleFamily === name)
    if (listed) return Promise.resolve(listed)
    let fetching = fetchingStandIns.get(name)
    if (!fetching) {
      fetching = api.addGoogleFont(name, false).then(
        (family) => {
          standIns.value = [...standIns.value, family]
          return family
        },
        () => null,
      )
      fetchingStandIns.set(name, fetching)
    }
    return fetching
  }

  return {
    families,
    library,
    loaded,
    busy,
    problem,
    isMine,
    load,
    family,
    upload,
    addFiles,
    rename,
    remove,
    addGoogle,
    standIn,
  }
})
